import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../services/user.service';
import { AuthService, LOGIN_TYPE } from '../services/auth.service';
import { TelemetryService } from '../services/telemetry.service';
import { TutorCruncherLoginService } from '../services/tutor-cruncher-login.service';
import { ThirdPartyLoginService } from '../services/third-party-login.service';
import { LoginPerformanceMetricsService } from '../services/login-performance-metrics.service';

// Enum for Clever error codes
export enum CleverErrorCode {
  INVALID_AUTHORIZATION_CODE = 'InvalidAuthorizationCode',
  NO_EMAIL = 'NoEmail',
}

export enum ClassLinkErrorCode {
  STUDENT_SIGNUP_NOT_ALLOWED = 'StudentSignupNotAllowed',
}

@Injectable({
  providedIn: 'root',
})
export class ThirdPartyLoginGuard implements CanActivate {
  constructor(
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly translate: TranslateService,
    private readonly telemetry: TelemetryService,
    private readonly tutorCruncherLoginService: TutorCruncherLoginService,
    private readonly thirdPartyLoginService: ThirdPartyLoginService,
    private readonly router: Router,
    private readonly loginMetrics: LoginPerformanceMetricsService,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    this.userService.setLoginFlowLoading(true);
    const { loginType, token, company_id, loginErrorCode, continueUrl } = route.queryParams;

    if (this.shouldRunSignOutCheck(loginType, token, company_id)) {
      const isUserSameAsLoggedIn = await this.thirdPartyLoginService.isTokenMatchingCurrentUser(
        token,
      );
      // If the user is not the same as the logged in user, sign out for API login
      const currentLoggedInFirebaseUser = this.authService.getCurrentUser();
      const telemetryData = {
        login_type: loginType,
        user_id: currentLoggedInFirebaseUser?.uid,
        user_email: currentLoggedInFirebaseUser?.email,
      };

      if (!isUserSameAsLoggedIn) {
        this.telemetry.logBasicEvent('third_party_login_different_user', {
          ...telemetryData,
          action: 'sign_out_for_api_login',
        });
        await this.thirdPartyLoginService.signOutForAPILogin();
      } else {
        // Log when user is same for cross-version analytics
        this.telemetry.logBasicEvent('third_party_login_same_user', {
          ...telemetryData,
          action: 'continue_without_sign_out',
        });
      }
    }

    if (loginType === LOGIN_TYPE.Clever && loginErrorCode === CleverErrorCode.NO_EMAIL) {
      this.handleCleverLoginNoEmailError();
    } else if (
      loginType === LOGIN_TYPE.Clever &&
      loginErrorCode === CleverErrorCode.INVALID_AUTHORIZATION_CODE
    ) {
      this.handleCleverLoginErrorInvalidAuthorizationCode();
    } else if (
      loginType === LOGIN_TYPE.Classlink &&
      loginErrorCode === ClassLinkErrorCode.STUDENT_SIGNUP_NOT_ALLOWED
    ) {
      this.handleClassLinkStudentSignupError();
    } else if (!token) {
      this.handleTokenLoginError();
    } else if (company_id) {
      await this.tutorCruncherLoginService.attemptTutorCruncherSignIn(company_id, token);
    } else if (this.isAuthorizedThirdPartyLoginType(loginType)) {
      // Start tracking performance for API token login flow
      if (this.isApiBasedLogin(loginType)) {
        this.loginMetrics.startTracking();
      }
      await this.attempCustomTokenLogin(token, continueUrl);
    } else {
      // If the specified third party login type is not supported by us, log an event and handle the error accordingly.
      this.telemetry.event('unsupported_third_party_login_type', { login_type: loginType });
      this.handleTokenLoginError();
    }

    return false;
  }

  private isAuthorizedThirdPartyLoginType(loginType: LOGIN_TYPE): boolean {
    return (Object.values(LOGIN_TYPE) as string[]).includes(loginType);
  }

  private async attempCustomTokenLogin(token: string, continueUrl: string) {
    // Parse packed token format (token~renewCode) if present
    const { rawToken, renewCode } = this.authService.parsePackedToken(token);

    try {
      await this.loginWithCustomTokenAndRedirect(rawToken, continueUrl);
    } catch (error) {
      // Only attempt renewal if renew code is available (indicates inspect mode)
      if (renewCode) {
        try {
          const exchanged = await this.thirdPartyLoginService.renewSpectatorToken(renewCode);
          if (exchanged) {
            await this.loginWithCustomTokenAndRedirect(exchanged, continueUrl);
            return;
          }
        } catch (renewError) {
          console.log({ ...renewError, code: 'INVALID_TOKEN_RENEWED' });
        }
      }

      console.log({ ...error, code: 'INVALID_TOKEN' });

      return this.handleTokenLoginError();
    }
  }

  private async loginWithCustomTokenAndRedirect(token: string, continueUrl: string): Promise<void> {
    await this.thirdPartyLoginService.loginWithCustomToken(token);
    this.router.navigateByUrl(continueUrl || this.authService.getRedirectUrl());
  }

  public async handleTokenLoginError() {
    await this.thirdPartyLoginService.signOutForAPILogin();
    const title = this.translate.instant('Invalid credentials');
    const message = this.translate.instant(
      "Sorry! We weren't able to log you in. Please try another log-in option.",
    );
    return this.thirdPartyLoginService.displayCustomLoginErrorToasterAndRoute(
      message,
      title,
      'INVALID_TOKEN',
    );
  }

  public async handleCleverLoginNoEmailError() {
    await this.thirdPartyLoginService.signOutForAPILogin();
    const title = this.translate.instant('Clever Login Not Approved');
    const message = this.translate.instant(
      'Your school or district has not approved your access to Clever. Please contact your school administrator for support',
    );
    return this.thirdPartyLoginService.displayCustomLoginErrorToasterAndRoute(
      message,
      title,
      'CLEVER_LOGIN_FAILED_NO_EMAIL',
    );
  }

  public async handleCleverLoginErrorInvalidAuthorizationCode() {
    await this.thirdPartyLoginService.signOutForAPILogin();
    const title = this.translate.instant('Clever Login Error');
    const message = this.translate.instant(
      'An error occurred while authorizing your login. Please try again',
    );
    return this.thirdPartyLoginService.displayCustomLoginErrorToasterAndRoute(
      message,
      title,
      'CLEVER_LOGIN_FAILED_INVALID_AUTHORIZATION_CODE',
    );
  }

  public async handleClassLinkStudentSignupError() {
    await this.thirdPartyLoginService.signOutForAPILogin();
    const title = this.translate.instant('Classlink Login Not Approved');
    const message = this.translate.instant(
      'You are not allowed to access this application. Please contact your teacher or administrator for access.',
    );
    return this.thirdPartyLoginService.displayCustomLoginErrorToasterAndRoute(
      message,
      title,
      'CLASSLINK_LOGIN_FAILED_STUDENT_SIGNUP_NOT_ALLOWED',
    );
  }

  private isApiBasedLogin(loginType: LOGIN_TYPE): boolean {
    return loginType === LOGIN_TYPE.APISignIn;
  }

  /**
   * Whether to sign out the current Firebase session before custom-token login.
   *
   * If someone is already signed in as user A and arrives with a token for user B,
   * we sign out first so signInWithCustomToken does not fail or leave a stale session.
   * When the token matches the current user, we keep the session and only log telemetry.
   *
   * Skipped when there is no token (nothing to compare), TutorCruncher is binding via
   * company_id (separate flow), or loginType is API (APISignIn bypasses this for perf).
   */
  private shouldRunSignOutCheck(
    loginType: LOGIN_TYPE,
    token: string | undefined,
    company_id: string | undefined,
  ): boolean {
    if (!token || company_id || this.isApiBasedLogin(loginType)) {
      return false;
    }
    return true;
  }
}
